// COMPSECTION: the ONE place a compliance onboarding task title is turned into display text.
//
// WHAT THIS SOLVES. The task formerly read "Upload background check for <employee>", describing an
// action the compliance officer can no longer take on its own: the upload now lives inside the Fill
// Compliance form (Section 4) and nowhere else. The job is to complete that section, so the task is
// now stored as "Complete compliance section for <employee>".
//
// WHY TRANSLATE HERE RATHER THAN STORE TWO TITLES (confirmed decision). Task.title is a plain
// stored string with no language attached, and the portal has no bilingual task titles anywhere.
// Storing one English title and translating at render means the label follows the language of the
// account READING the list - the compliance officer, who owns the task - rather than the entity of
// the employee it happens to be about. It also leaves exactly one wording for the cleanup script to
// write, so stored data cannot drift between the two languages.
//
// THE STORED TITLE IS NEVER REWRITTEN BY THIS FUNCTION. It is display only: an unrecognised title
// is returned exactly as it came from the server, so a task carrying a bespoke title (the
// intern-conversion path passes its own, naming why the task appeared) still reads correctly.

// The stored English wording, as the backend writes it. Kept as a pattern rather than a prefix
// match so a title that merely mentions these words is not captured by accident.
const STORED_PREFIX = 'Complete compliance section for ';
// BGCLEANUP: the intern-conversion wording, "Classify the background check for <name> (Section 4A),
// converted from intern to <type>". The type is the stored employee type, shown as stored.
// CLASSMAIL: the current wordings, "Complete the classification for <name> (Section 4A)" and its
// intern-conversion form; the two earlier ones are still recognised for tasks already stored.
const INTERN_CONVERSION = /^(?:Classify the background check|Complete the classification) for (.+) \(Section 4A\), converted from intern to (.+)$/;
const CLASSIFICATION = /^Complete the classification for (.+) \(Section 4A\)$/;

/**
 * Display text for a task title.
 *
 * `title` the stored title, straight from the server.
 * `t`     the i18next translate function from useTranslation().
 *
 * Returns the translated label when the title is the standard compliance-section wording, and the
 * title unchanged otherwise. Never throws: a missing title yields an empty string, and a missing
 * translator returns the stored title, because a label is never worth breaking a list render over.
 */
export function complianceTaskTitle(title, t) {
  if (!title) return '';
  if (typeof t !== 'function') return title;
  const conv = INTERN_CONVERSION.exec(title);
  if (conv) return t('compliance.complianceInternConversionTask', { name: conv[1].trim(), type: conv[2].trim() });
  const cls = CLASSIFICATION.exec(title);
  if (cls && cls[1].trim()) return t('compliance.complianceSectionTask', { name: cls[1].trim() });
  if (!title.startsWith(STORED_PREFIX)) return title;
  const name = title.slice(STORED_PREFIX.length).trim();
  if (!name) return title;
  return t('compliance.complianceSectionTask', { name });
}

export default complianceTaskTitle;
